/**
 * Organic Tree Layout Algorithm
 *
 * Creates a left-to-right tree layout with organic spacing and natural flow.
 * Each node is positioned based on its depth (x) and its vertical position
 * among siblings (y), with automatic collision avoidance.
 *
 * Features:
 * - Left-to-right horizontal flow
 * - Automatic vertical spacing based on subtree heights
 * - Parent nodes centered vertically among their children
 * - Support for nodes with multiple parents (DAG structure)
 * - Configurable spacing and dimensions
 */

const DEFAULT_CONFIG = {
  cardWidth: 280,
  cardHeight: 120,
  horizontalGap: 200,  // Gap between depth levels (increased for start node spacing)
  verticalGap: 40,     // Minimum gap between siblings
  rootX: 50,           // Starting X position for root
  rootY: 300,          // Starting Y position for root (center-ish)
};

/**
 * Calculate the height (in pixels) of the entire subtree rooted at this node.
 * This includes the node itself plus all its descendants with proper spacing.
 *
 * @param {Object} node - Node with children array
 * @param {Object} nodeMap - Map of nodeId to node object
 * @param {Object} config - Layout configuration
 * @returns {number} Total height of subtree in pixels
 */
/**
 * How tall this node's own row is. A blocker card sits in the column before the
 * node and can stack several blockers, so a blocked node's row is as tall as
 * whichever of the two is bigger — otherwise a tall card would overlap the rows
 * above and below it.
 */
function ownRowHeight(node, config) {
  const blockerHeight = config.blockerHeights?.get(node.id) || 0;
  return Math.max(config.cardHeight, blockerHeight);
}

function calculateSubtreeHeight(node, nodeMap, config) {
  if (!node.children || node.children.length === 0) {
    // Leaf node: just its own height (or its blocker card's, if that is taller)
    return ownRowHeight(node, config);
  }

  // Calculate heights of all child subtrees
  const childHeights = node.children.map(childId => {
    const child = nodeMap[childId];
    if (!child) return config.cardHeight; // Fallback for missing nodes
    return Math.max(calculateSubtreeHeight(child, nodeMap, config), ownRowHeight(child, config));
  });

  // Total height = sum of all child heights + gaps between them
  const totalChildrenHeight = childHeights.reduce((sum, h) => sum + h, 0);
  const totalGaps = (node.children.length - 1) * config.verticalGap;

  return Math.max(totalChildrenHeight + totalGaps, ownRowHeight(node, config));
}

/**
 * Position nodes in a left-to-right tree layout with organic spacing.
 * Uses a top-down pass after calculating subtree heights bottom-up.
 *
 * @param {Object} rootNode - Root node of the tree
 * @param {Object} nodeMap - Map of nodeId to node object
 * @param {Object} config - Layout configuration
 * @returns {Object} Map of nodeId to {x, y} positions
 */
function positionNodes(rootNode, nodeMap, config) {
  const positions = {};

  // Helper function for recursive positioning
  function positionSubtree(node, x, y) {
    if (!node) return;

    // ANTI-REFLOW: position purely by the PRIMARY-parent tree (the x passed in).
    // Secondary parents (pathway_connections / borrowed nodes) must NOT move a
    // node — they are drawn as dashed overlay edges only. Repositioning a node
    // to the right of its rightmost parent caused the whole map to thrash
    // whenever a node gained a second parent (the original multi-parent blocker).
    // See specs/systems-analysis-visualisation.md §9.
    const actualX = x;

    // Calculate subtree height for this node
    const subtreeHeight = calculateSubtreeHeight(node, nodeMap, config);

    // Position this node centered in its subtree's vertical space
    const nodeY = y + (subtreeHeight / 2) - (config.cardHeight / 2);
    // (subtreeHeight already accounts for a taller blocker row, so the node
    // stays centred in the space its own row occupies.)
    positions[node.id] = { x: actualX, y: nodeY };

    // If no children, we're done
    if (!node.children || node.children.length === 0) {
      return;
    }

    // Position children at next depth level
    // Use actualX (adjusted for multi-parent) instead of x
    const columnStep = config.cardWidth + config.horizontalGap;
    const childX = actualX + columnStep;
    let currentY = y; // Start at top of subtree

    node.children.forEach(childId => {
      const child = nodeMap[childId];
      if (!child) return;

      // Calculate this child's subtree height
      const childSubtreeHeight = calculateSubtreeHeight(child, nodeMap, config);

      // A blocked edge gets its own column: the blocker card sits where a node
      // would, and the child (with everything under it) moves one level deeper.
      // Squeezing the card into the gap between two columns is what made the
      // map feel crowded — a blocker is an object, so it gets an object's room.
      const blockedHere = config.blockedEdges?.has(`${node.id}->${childId}`);
      // A node something blocks belongs after the block. config.extraDepth says
      // how many further columns this child (and everything under it) has to
      // move so every blocking line runs left to right like the rest of the
      // map. Worked out by the renderer, which has to lay the map out once to
      // find out where the blockers landed.
      const pushed = (config.extraDepth?.[childId] || 0) * columnStep;
      const childColumnX = (blockedHere ? childX + columnStep : childX) + pushed;

      // Position child's subtree
      positionSubtree(child, childColumnX, currentY);

      // Move down for next sibling
      currentY += childSubtreeHeight + config.verticalGap;
    });
  }

  // Start positioning from root
  positionSubtree(rootNode, config.rootX, config.rootY);

  return positions;
}

/**
 * Generate Bézier curve path for an arrow connecting parent to child.
 * Creates a smooth horizontal curve with control points 1/3 of the way.
 *
 * @param {Object} from - {x, y} position of parent node center
 * @param {Object} to - {x, y} position of child node center
 * @param {Object} config - Layout configuration
 * @returns {string} SVG path d attribute
 */
function generateArrowPath(from, to, config) {
  // Positions are CENTER positions (nodes are rendered with rect at -width/2, -height/2)
  // So we need to calculate edge positions from the center
  const fromCenterX = from.x;
  const fromCenterY = from.y;
  const toCenterX = to.x;
  const toCenterY = to.y;

  const buttonGap = 36; // Distance from node edge to (+) button center

  // Calculate connection points (right edge of parent, left edge of child)
  const startX = fromCenterX + config.cardWidth / 2;  // Right edge from center
  const startY = fromCenterY;                          // Center Y

  // Waypoint: Parent's (+) button position
  const buttonX = startX + buttonGap;
  const buttonY = startY;

  const endX = toCenterX - config.cardWidth / 2;       // Left edge from center
  const endY = toCenterY;                              // Center Y

  // Create path routing through the (+) button
  // Segment 2: From (+) button to child edge using Bezier curve
  const horizontalDistance = endX - buttonX;
  const segment2Control1X = buttonX + horizontalDistance / 3;
  const segment2Control1Y = buttonY;
  const segment2Control2X = endX - horizontalDistance / 3;
  const segment2Control2Y = endY;

  // Build the path: Move to start, line to button, then Bezier curve to end
  return `M ${startX},${startY} L ${buttonX},${buttonY} C ${segment2Control1X},${segment2Control1Y} ${segment2Control2X},${segment2Control2Y} ${endX},${endY}`;
}

/**
 * Main layout function: Calculate positions and arrows for organic tree.
 *
 * @param {Array} nodes - Array of node objects with {id, parentIds, children}
 * @param {Object} customConfig - Optional config overrides
 * @returns {Object} {positions: {nodeId: {x, y}}, arrows: [{from, to, path}]}
 */
export function layoutOrganicTree(nodes, customConfig = {}) {
  const config = { ...DEFAULT_CONFIG, ...customConfig };

  console.log('🌳 layoutOrganicTree: Starting with', nodes.length, 'nodes');

  // Build node map for quick lookup
  const nodeMap = {};
  nodes.forEach(node => {
    nodeMap[node.id] = node;
  });

  // Find root node (node that isn't anyone's child)
  const childIds = new Set();
  nodes.forEach(node => {
    if (node.children) {
      node.children.forEach(childId => childIds.add(childId));
    }
  });

  // A map is a forest, not a tree: the instance view holds one root per
  // scenario, and a workshop that used four scenarios has four. Positioning
  // only the first left the other three trees — 390 of K8's 544 nodes —
  // without positions, stacked at the origin as what looked like one stray
  // card. Every root gets its subtree laid out, stacked top to bottom, the
  // largest first so the map's centre of mass stays near the top-left.
  const roots = nodes.filter(node => !childIds.has(node.id));

  if (!roots.length) {
    console.error('❌ No root node found in tree');
    return { positions: {}, arrows: [] };
  }

  roots.sort((a, b) => calculateSubtreeHeight(b, nodeMap, config) - calculateSubtreeHeight(a, nodeMap, config));
  console.log(`✅ ${roots.length} root(s):`, roots.map(r => r.id));

  const positions = {};
  let y = config.rootY;
  for (const root of roots) {
    const h = calculateSubtreeHeight(root, nodeMap, config);
    Object.assign(positions, positionNodes(root, nodeMap, { ...config, rootY: y }));
    y += h + config.verticalGap * 4;
  }

  console.log('📍 Positions calculated:', Object.keys(positions).length, 'nodes positioned');
  console.log('🗺️ All positioned nodes:', Object.keys(positions));
  console.log('❌ Nodes WITHOUT positions:', nodes.filter(n => !positions[n.id]).map(n => n.id));

  // Generate arrows from each parent to child
  // Use parentIds to support multi-parent (DAG) structures
  const arrows = [];
  nodes.forEach(node => {
    if (!node.parentIds || node.parentIds.length === 0) return;

    node.parentIds.forEach(parentId => {
      const parentPos = positions[parentId];
      const childPos = positions[node.id];

      if (parentPos && childPos) {
        const blockedHere = config.blockedEdges?.has(`${parentId}->${node.id}`);
        arrows.push({
          from: parentId,
          to: node.id,
          path: generateArrowPath(parentPos, childPos, config),
          // The blocker occupies the column the child would have used, so it
          // lines up with the other cards at that depth — it is a node layer,
          // not something floating between layers. Positions are card centres,
          // so this is exactly one column step from the parent.
          // Derived from the CHILD, not the parent: the child is always exactly
          // one column past its blocker, so if anything pushes the child deeper
          // the blocker travels with it instead of staying pinned beside a
          // parent it no longer sits next to.
          ...(blockedHere ? {
            blockerColumn: {
              x: childPos.x - (config.cardWidth + config.horizontalGap),
              y: childPos.y,
            },
          } : {}),
        });
      }
    });
  });

  console.log('🏹 Arrows generated:', arrows.length, 'arrows');
  if (arrows.length > 0) {
    console.log('Sample arrow:', arrows[0]);
  }

  return { positions, arrows };
}

/**
 * Calculate bounding box for the entire tree layout.
 * Useful for centering or determining canvas size.
 *
 * @param {Object} positions - Map of nodeId to {x, y}
 * @param {Object} config - Layout configuration
 * @returns {Object} {minX, minY, maxX, maxY, width, height}
 */
export function calculateBoundingBox(positions, config = DEFAULT_CONFIG) {
  const posArray = Object.values(positions);
  if (posArray.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  const minX = Math.min(...posArray.map(p => p.x - config.cardWidth / 2));
  const maxX = Math.max(...posArray.map(p => p.x + config.cardWidth / 2));
  const minY = Math.min(...posArray.map(p => p.y - config.cardHeight / 2));
  const maxY = Math.max(...posArray.map(p => p.y + config.cardHeight / 2));

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}

export default layoutOrganicTree;
